import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

const OVERLAY_MS = 750;

function FlipWord() {
  const spin = { duration: 0.6, repeat: Infinity, ease: "linear" };
  const letter = "inline-block font-heading text-7xl sm:text-9xl leading-none";
  return (
    <div className="flex items-center gap-1 sm:gap-2" style={{ perspective: 900 }} data-testid="flip-word">
      {"FLI".split("").map((c, i) => (
        <motion.span key={`fli-${c}-${i}`} className={`${letter} text-white`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03, duration: 0.25 }}>{c}</motion.span>
      ))}
      {/* Left P: View A blue, spins clockwise */}
      <motion.span
        className={letter}
        style={{ transformStyle: "preserve-3d", color: "#7B2CBF", backfaceVisibility: "visible" }}
        animate={{ rotateY: [0, 360] }}
        transition={spin}
        data-testid="flip-p-left"
      >P</motion.span>
      {/* Right P: View B coral, starts as the mirror image (rotateY 180) and spins the opposite way */}
      <motion.span
        className={letter}
        style={{ transformStyle: "preserve-3d", color: "#FFC300", backfaceVisibility: "visible" }}
        animate={{ rotateY: [180, -180] }}
        transition={spin}
        data-testid="flip-p-right"
      >P</motion.span>
      {"ED".split("").map((c, i) => (
        <motion.span key={`ed-${c}-${i}`} className={`${letter} text-white`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 + i * 0.03, duration: 0.25 }}>{c}</motion.span>
      ))}
    </div>
  );
}

export default function PageTransition({ children }) {
  const location = useLocation();
  const reduced = useReducedMotion();
  const [show, setShow] = useState(false);
  const prevPath = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const prev = prevPath.current;
    prevPath.current = location.pathname;
    // Skip first mount and StrictMode double-invocation (prev null or unchanged path)
    if (prev === null || prev === location.pathname) return;
    if (reduced) return;
    setShow(true);
    const t = setTimeout(() => setShow(false), OVERLAY_MS);
    return () => clearTimeout(t);
  }, [location.pathname, reduced]);

  return (
    <>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.35, delay: show ? 0.3 : 0, ease: "easeOut" } }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
        >
          {children}
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {show && (
          <motion.div
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-dark"
            style={{ backgroundColor: "#172033" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.18 } }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
            data-testid="page-transition-overlay"
          >
            <FlipWord />
            <motion.span
              className="mt-4 text-[11px] font-semibold tracking-[0.3em] uppercase text-white/40"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            >
              view
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
